import type { Market, MarketsQueryParams, PaginatedResponse } from '@/types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS } from '@/constants';

/**
 * Markets Tags API - Single source for all /markets/tags endpoints
 * This API handles all tag-based market queries
 */
export const marketsTagsApi = {
  /**
   * Get markets by category slug (with full API path)
   * @param slug - Category slug (e.g., 'trending', 'breaking', 'sports')
   */
  getBySlug: async (slug: string) => {
    const response = await axiosClient.get(API_ENDPOINTS.MARKETS_TAGS_BY_SLUG(slug));
    return response.data;
  },

  /**
   * Get markets by category slug with query parameters
   * @param slug - Category slug
   * @param params - Optional query parameters for filtering
   */
  getMarketsWithParams: async (slug: string, params?: MarketsQueryParams): Promise<Market[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.MARKETS_TAGS_BY_SLUG(slug), {
      params: {
        ...params,
        category: undefined, // Remove category from params as it's in the URL
      },
    });

    // Handle the API response structure from /markets/tags/{slug}
    // Response has: { success: true, data: { ...tags, marketsCount: number, markets: Market[] } }
    if (response.data?.success && response.data?.data?.markets) {
      return response.data.data.markets;
    }

    // Fallback if structure is different
    return response.data?.data?.markets || response.data?.markets || [];
  },

  /**
   * Get paginated markets by category slug
   * @param slug - Category slug
   * @param params - Optional query parameters including pagination
   */
  getPaginated: async (
    slug: string,
    params?: MarketsQueryParams
  ): Promise<PaginatedResponse<Market>> => {
    const page = params?.page || 1;
    const limit = params?.limit || 10;

    const response = await axiosClient.get(API_ENDPOINTS.MARKETS_TAGS_BY_SLUG(slug), {
      params: {
        ...params,
        category: undefined, // Remove category from params as it's in the URL
        page,
        limit,
      },
    });

    // Handle the API response structure
    if (response.data?.success && response.data?.data) {
      const { marketsCount, markets } = response.data.data;
      const totalPages = Math.ceil((marketsCount || 0) / limit);

      return {
        items: markets || [],
        total: marketsCount || 0,
        page,
        limit,
        totalPages,
        hasNextPage: page < totalPages,
        hasPrevPage: page > 1,
      };
    }

    // Fallback
    return {
      items: [],
      total: 0,
      page,
      limit,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    };
  },

  /**
   * Get trending markets (convenience method)
   */
  getTrending: async (): Promise<Market[]> => {
    return marketsTagsApi.getMarketsWithParams('trending');
  },

  /**
   * Get breaking markets (markets with biggest price changes in 24h)
   */
  getBreaking: async (): Promise<Market[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.MARKETS_TAGS_BREAKING);

    if (response.data?.success && response.data?.data?.markets) {
      return response.data.data.markets;
    }

    return response.data?.markets || [];
  },
};
